import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const wsRef = useRef(null);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      setUser(response.data.user);
      setProfile(response.data.profile);
      setIsAuthenticated(true);
      
      // Connect WebSocket
      connectWebSocket(token);
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  const connectWebSocket = (token) => {
    if (wsRef.current) return;
    
    const wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    wsRef.current = new WebSocket(`${wsUrl}/ws/${token}`);
    
    wsRef.current.onmessage = (event) => {
      const data = JSON.parse(event.data);
      // Dispatch custom event for components to listen
      window.dispatchEvent(new CustomEvent('ws-message', { detail: data }));
    };

    wsRef.current.onclose = () => {
      wsRef.current = null;
      // Reconnect after 5 seconds
      setTimeout(() => {
        const currentToken = localStorage.getItem('token');
        if (currentToken) connectWebSocket(currentToken);
      }, 5000);
    };
  };

  const sendWsMessage = (message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  };

  useEffect(() => {
    checkAuth();
    return () => {
      if (wsRef.current) wsRef.current.close();
    };
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password }, { withCredentials: true });
    const { token, user: userData } = response.data;
    localStorage.setItem('token', token);
    setUser(userData);
    setIsAuthenticated(true);
    connectWebSocket(token);
    return response.data;
  };

  const register = async (data) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, data, { withCredentials: true });
    const { token, user: userData } = response.data;
    localStorage.setItem('token', token);
    setUser(userData);
    setIsAuthenticated(true);
    connectWebSocket(token);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.error('Logout error:', e);
    }
    localStorage.removeItem('token');
    setUser(null);
    setProfile(null);
    setIsAuthenticated(false);
    if (wsRef.current) wsRef.current.close();
  };

  const updateProfile = (newProfile) => {
    setProfile(newProfile);
  };

  const handleGoogleCallback = async (sessionId) => {
    const response = await axios.post(`${API_URL}/api/auth/session`, { session_id: sessionId }, { withCredentials: true });
    const { token, user: userData } = response.data;
    localStorage.setItem('token', token);
    setUser(userData);
    setIsAuthenticated(true);
    connectWebSocket(token);
    return response.data;
  };

  return (
    <AuthContext.Provider value={{
      user,
      profile,
      loading,
      isAuthenticated,
      login,
      register,
      logout,
      checkAuth,
      updateProfile,
      handleGoogleCallback,
      sendWsMessage
    }}>
      {children}
    </AuthContext.Provider>
  );
};
